import { createFileRoute, Link } from "@tanstack/react-router";
import {
  ArrowLeft,
  ArrowRight,
  BadgePercent,
  Banknote,
  CheckCircle2,
  Clock3,
  Gift,
  Landmark,
  Link2,
  Megaphone,
  ShieldCheck,
  Wallet,
  XCircle,
} from "lucide-react";
import { BusinessInfoCard } from "@/components/business-info-block";

const PAGE_TITLE = "Hướng Dẫn Hoa Hồng Giới Thiệu - Nhận 10% Trọn Đời | Bói Đê";
const PAGE_DESC =
  "Hướng dẫn chi tiết chương trình hoa hồng Bói Đê: đặt mã giới thiệu, nhận 10% trọn đời mỗi lần bạn bè nạp, tạo mã chiến dịch và rút tiền về tài khoản ngân hàng.";

export const Route = createFileRoute("/huong-dan-hoa-hong")({
  head: () => ({
    meta: [
      { title: PAGE_TITLE },
      { name: "description", content: PAGE_DESC },
      { property: "og:title", content: PAGE_TITLE },
      { property: "og:description", content: PAGE_DESC },
      { property: "og:url", content: "https://boide.online/huong-dan-hoa-hong" },
    ],
    links: [{ rel: "canonical", href: "https://boide.online/huong-dan-hoa-hong" }],
  }),
  component: HoaHongGuidePage,
});

const STEPS = [
  {
    icon: BadgePercent,
    title: "1. Đặt mã giới thiệu",
    desc: "Vào trang Giới thiệu & Hoa hồng, tự đặt mã của riêng bạn (vd: cuan-tiktok). Hệ thống tạo link dạng boide.online/?ref=<mã>.",
  },
  {
    icon: Link2,
    title: "2. Chia sẻ link",
    desc: "Gửi link cho bạn bè hoặc gắn vào bài viết, video, tiểu sử mạng xã hội. Người bấm link và đăng ký sẽ được gắn với mã của bạn.",
  },
  {
    icon: Gift,
    title: "3. Nhận hoa hồng",
    desc: "Mỗi lần người được giới thiệu nạp tiền — dù là lần thứ bao nhiêu — bạn nhận 10% giá trị nạp vào số dư hoa hồng, trọn đời.",
  },
  {
    icon: Landmark,
    title: "4. Rút hoặc chuyển",
    desc: "Hoa hồng có thể chuyển sang ví bói để dùng ngay, hoặc gửi yêu cầu rút về tài khoản ngân hàng của bạn.",
  },
];

function HoaHongGuidePage() {
  return (
    <main className="flex-1 min-h-0 overflow-y-auto px-4 py-6 pb-24 md:px-8 md:py-10 md:pb-10">
      <div className="mx-auto max-w-4xl space-y-6">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-sm text-muted-foreground transition hover:text-primary"
        >
          <ArrowLeft className="h-4 w-4" />
          Về trang chủ
        </Link>

        {/* Hero */}
        <section className="rounded-3xl border border-gold-soft bg-card/70 p-6 shadow-mystic backdrop-blur md:p-8">
          <span className="font-sans text-[11px] tracking-[0.18em] font-semibold text-primary uppercase">
            Hướng dẫn
          </span>
          <h1 className="mt-3 font-serif-soft text-3xl text-foreground md:text-4xl">
            Hoa hồng giới thiệu — <span className="text-gold-gradient">nhận 10% trọn đời</span>
          </h1>
          <p className="mt-3 text-sm leading-relaxed text-muted-foreground md:text-base">
            Chia sẻ Bói Đê với bạn bè, bạn nhận{" "}
            <b className="text-primary">10% hoa hồng trọn đời</b> mỗi lần họ nạp tiền — người được
            giới thiệu cũng được <b className="text-primary">+10% cho lần nạp đầu</b>. Hoa hồng có
            thể chuyển sang ví bói hoặc rút thẳng về tài khoản ngân hàng.
          </p>
          <Link
            to="/gioi-thieu"
            className="group mt-5 inline-flex items-center gap-2 rounded-full bg-gold-gradient px-6 py-3 text-sm font-semibold text-primary-foreground shadow-gold transition hover:scale-[1.02] active:scale-95"
          >
            Lấy mã giới thiệu của tôi
            <ArrowRight className="h-4 w-4 transition group-hover:translate-x-0.5" />
          </Link>
        </section>

        {/* 4 bước */}
        <section className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {STEPS.map((s) => (
            <article
              key={s.title}
              className="rounded-2xl border border-gold-soft/50 bg-card/60 p-5 backdrop-blur"
            >
              <s.icon className="h-6 w-6 text-primary" />
              <h2 className="mt-3 font-serif-soft text-lg text-foreground">{s.title}</h2>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{s.desc}</p>
            </article>
          ))}
        </section>

        {/* 2 loại tiền */}
        <section className="rounded-2xl border border-gold-soft/50 bg-card/60 p-5 backdrop-blur md:p-6">
          <h2 className="font-serif-soft text-xl text-gold-gradient">
            Ví bói và số dư hoa hồng khác nhau thế nào?
          </h2>
          <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="rounded-xl border border-border/50 bg-background/40 p-4">
              <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
                <Wallet className="h-4 w-4 text-primary" /> Ví bói
              </div>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                Tiền dùng để xem bói: gieo quẻ, khai mở lá số, Bói Live... Nhận từ nạp tiền, điểm
                danh, quà sự kiện. <b>Không rút được</b> về ngân hàng.
              </p>
            </div>
            <div className="rounded-xl border border-primary/40 bg-gold-gradient/10 p-4">
              <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
                <Gift className="h-4 w-4 text-primary" /> Số dư hoa hồng
              </div>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                Tiền thật bạn kiếm được từ giới thiệu. Có thể{" "}
                <b className="text-primary">chuyển sang ví bói</b> để dùng ngay hoặc{" "}
                <b className="text-primary">rút về tài khoản ngân hàng</b>.
              </p>
            </div>
          </div>
        </section>

        {/* Mã chiến dịch */}
        <section className="rounded-2xl border border-gold-soft/50 bg-card/60 p-5 backdrop-blur md:p-6">
          <h2 className="flex items-center gap-2 font-serif-soft text-xl text-gold-gradient">
            <Megaphone className="h-5 w-5 text-primary" /> Nâng cao: mã chiến dịch
          </h2>
          <div className="mt-3 space-y-3 text-sm leading-relaxed text-muted-foreground md:text-base">
            <p>
              Khi hoa hồng tích luỹ đạt <b className="text-primary">100.000đ</b>, bạn mở khoá quyền
              tạo <b>mã chiến dịch</b> — công cụ dành cho người làm nội dung, chạy nhóm, KOL/KOC:
            </p>
            <ul className="list-disc space-y-1.5 pl-5">
              <li>
                Bạn tự đặt <b>% khuyến mãi cho người nạp</b> (tối đa 20%) và số lượt sử dụng — người
                nạp nhập mã ở trang nạp tiền để nhận khuyến mãi, nên mã rất dễ chia sẻ.
              </li>
              <li>
                Mỗi lượt nạp dùng mã, bạn vẫn hưởng <b className="text-primary">10% hoa hồng</b>.
              </li>
              <li>
                Ngân sách khuyến mãi cấp theo <b>bậc</b>: hoa hồng tích luỹ càng cao, quỹ tạo mã
                càng lớn. Mã hết lượt sẽ tự tắt và hoàn quỹ để bạn tạo mã mới.
              </li>
            </ul>
          </div>
        </section>

        {/* Rút tiền */}
        <section className="rounded-2xl border border-gold-soft/50 bg-card/60 p-5 backdrop-blur md:p-6">
          <h2 className="flex items-center gap-2 font-serif-soft text-xl text-gold-gradient">
            <Banknote className="h-5 w-5 text-primary" /> Rút tiền về tài khoản ngân hàng
          </h2>
          <div className="mt-3 space-y-3 text-sm leading-relaxed text-muted-foreground md:text-base">
            <p>
              Tại trang <b>Giới thiệu & Hoa hồng</b>, mục <b>Rút tiền hoa hồng</b>: nhập tên ngân
              hàng, số tài khoản, tên chủ tài khoản và số tiền muốn rút (tối thiểu{" "}
              <b className="text-primary">100.000đ</b>) rồi bấm <b>Gửi yêu cầu</b>. Số tiền được tạm
              giữ khỏi số dư hoa hồng và đơn chuyển cho admin xử lý.
            </p>
            <ul className="space-y-2">
              <li className="flex items-start gap-2">
                <Clock3 className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" />
                <span>
                  <b className="text-foreground">Chờ xử lý</b> — admin đã nhận yêu cầu và sẽ chuyển
                  khoản trong <b>1–3 ngày làm việc</b>.
                </span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500" />
                <span>
                  <b className="text-foreground">Đã chuyển</b> — tiền đã tới tài khoản ngân hàng của
                  bạn.
                </span>
              </li>
              <li className="flex items-start gap-2">
                <XCircle className="mt-0.5 h-4 w-4 shrink-0 text-red-400" />
                <span>
                  <b className="text-foreground">Từ chối</b> — thường do sai thông tin tài khoản. Số
                  tiền được <b>hoàn lại đầy đủ</b> vào số dư hoa hồng kèm lý do; bạn kiểm tra lại
                  STK và gửi yêu cầu mới.
                </span>
              </li>
            </ul>
            <p>
              Trạng thái từng đơn hiển thị ngay dưới form rút, mục <b>“Đơn rút của bạn”</b> — không
              cần nhắn tin hỏi.
            </p>
          </div>
        </section>

        {/* Lưu ý */}
        <section className="rounded-2xl border border-gold-soft/50 bg-card/60 p-5 backdrop-blur md:p-6">
          <h2 className="flex items-center gap-2 font-serif-soft text-xl text-gold-gradient">
            <ShieldCheck className="h-5 w-5 text-primary" /> Lưu ý quan trọng
          </h2>
          <ul className="mt-3 list-disc space-y-1.5 pl-5 text-sm leading-relaxed text-muted-foreground md:text-base">
            <li>
              Mã giới thiệu gắn với tài khoản <b>ngay lúc đăng ký</b> và không đổi được sau đó — hãy
              nhắc bạn bè nhập mã (hoặc vào bằng link) trước khi tạo tài khoản.
            </li>
            <li>Không thể tự giới thiệu chính mình; hệ thống chặn các hành vi gian lận.</li>
            <li>
              Chuyển hoa hồng sang ví bói là <b>một chiều</b> — không chuyển ngược từ ví bói về số
              dư hoa hồng.
            </li>
            <li>
              Vui lòng điền chính xác <b>tên chủ tài khoản</b> khi rút tiền — sai thông tin là lý do
              từ chối phổ biến nhất.
            </li>
          </ul>
        </section>

        {/* CTA cuối */}
        <section className="rounded-3xl border border-gold-soft bg-card/70 p-6 text-center shadow-mystic backdrop-blur md:p-8">
          <h2 className="font-serif-soft text-2xl text-foreground">Sẵn sàng nhận hoa hồng?</h2>
          <p className="mx-auto mt-2 max-w-xl text-sm text-muted-foreground md:text-base">
            Đặt mã trong 30 giây, chia sẻ link và theo dõi hoa hồng đổ về theo thời gian thực.
          </p>
          <Link
            to="/gioi-thieu"
            className="group mt-4 inline-flex items-center gap-2 rounded-full bg-gold-gradient px-6 py-3 text-sm font-semibold text-primary-foreground shadow-gold transition hover:scale-[1.02] active:scale-95"
          >
            Đến trang Giới thiệu & Hoa hồng
            <ArrowRight className="h-4 w-4 transition group-hover:translate-x-0.5" />
          </Link>
        </section>

        <BusinessInfoCard />
      </div>
    </main>
  );
}
